import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {BenchlingExpressionFunction} from 'benchling-expressions/BenchlingExpressionTypes';
import {BenchlingFunctionSignature} from 'benchling-expressions/BenchlingFunctionSignatures';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, GRAY_SEMIDARK} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {immutableRemoveAt} from 'util/ImmutableListUtil';
import {DeepPartial} from 'util/Types';

export interface DeleteConditionButtonProps {
  idx: number;
  expression: DeepPartial<BenchlingExpressionFunction> | null;
  setExpression: (newExpression: DeepPartial<BenchlingExpressionFunction>) => void;
  defaultOperator: BenchlingFunctionSignature;
  recursiveDepth: number;
  isExpressionBoolean: boolean;
  dataTestAttrs?: DataTestAttrs;
}

export default function DeleteConditionButton(props: DeleteConditionButtonProps): JSX.Element | null {
  const {disabled, text, buttonStyle} = useMemo(() => {
    assert(props.expression != null, 'Expected expression to be defined');
    const disabled = (props.expression?.parameters?.length ?? 0) <= 1;
    let text;
    if (props.recursiveDepth > 0) {
      text = disabled ? 'Condition groups require at least one condition' : 'Delete condition';
    } else {
      text = disabled
        ? 'Routers require at least one condition'
        : props.isExpressionBoolean
          ? 'Delete condition group'
          : 'Delete condition';
    }

    const buttonStyle = disabled ? styles.disabled : styles.enabled;

    return {
      disabled,
      text,
      buttonStyle,
    };
  }, [props.expression, props.recursiveDepth, props.isExpressionBoolean]);

  function deleteExpression(): void {
    assert(props.expression != null, 'Expected expression to be defined');
    assert(
      props.expression.parameters != null && props.expression.parameters.length > 1,
      'Expected multiple router expression parameters'
    );

    const newParams = immutableRemoveAt(props.expression.parameters, props.idx);

    props.setExpression({
      ...props.expression,
      name: newParams.length === 1 ? props.defaultOperator.functionName : props.expression.name,
      parameters: newParams,
    });
  }

  return (
    <div
      className={css(props.isExpressionBoolean && styles.conditionGroup)}
      {...getTestProps(props.dataTestAttrs)}
    >
      <Tooltip text={text}>
        <Button
          icon="delete"
          buttonStyle={ButtonStyle.TRANSPARENT}
          className={classNames(css(buttonStyle))}
          onClick={deleteExpression}
          disabled={disabled}
          size={ButtonSize.MEDIUM}
        />
      </Tooltip>
    </div>
  );
}

const styles = StyleSheet.create({
  enabled: {
    color: GRAY_SEMIDARK,
  },
  disabled: {
    color: GRAY,
  },
  conditionGroup: {
    margin: '10px -46px',
    height: 'fit-content',
  },
  condition: {
    margin: '0px',
  },
});
